<div class="adf-page">
  <div class="adf-hero">
    <p class="adf-hero-text">
      Organize field groups, reorder fields, and define visibility, editability, and mandatory rules.
    </p>
    <div class="adf-hero-stats" *ngIf="!pagerBusy && groupedDataFields?.length">
      <div class="adf-stat">
        <span class="adf-stat-value">{{ groupedDataFields.length }}</span>
        <span class="adf-stat-label">Groups</span>
      </div>
      <div class="adf-stat">
        <span class="adf-stat-value">{{ pagingDetails.total || 0 }}</span>
        <span class="adf-stat-label">Rules</span>
      </div>
    </div>
  </div>

  <div class="adf-loader" *ngIf="pagerBusy">
    <app-ltms-loader></app-ltms-loader>
  </div>

  <div class="adf-content" *ngIf="!pagerBusy">
    <div class="adf-control-bar">
      <div class="adf-control-bar-main">
        <button type="button" [disabled]="readonly" class="adf-btn adf-btn--primary" (click)="addNewField()">
          New {{ viewMode === 'basic' ? 'Group' : 'Rule' }}
        </button>

        <div class="adf-control-divider" aria-hidden="true"></div>

        <div class="adf-view-toggle" role="tablist" aria-label="View mode">
          <button type="button" role="tab" class="adf-toggle-btn"
            [class.active]="viewMode === 'basic'"
            [attr.aria-selected]="viewMode === 'basic'"
            (click)="setViewMode('basic')">Fields</button>
          <button type="button" role="tab" class="adf-toggle-btn"
            [class.active]="viewMode === 'advanced'"
            [attr.aria-selected]="viewMode === 'advanced'"
            (click)="setViewMode('advanced')">Rules</button>
        </div>
      </div>

      <div class="adf-control-bar-tools">
        <div class="adf-filter" *ngIf="viewMode === 'advanced'">
          <select id="ruleTypeFilter" class="adf-select" [(ngModel)]="lwDataFieldWorkflowActionsList"
            (change)="filterRulesByType(lwDataFieldWorkflowActionsList, true)" aria-label="Filter by rule type">
            <option [value]="0">All types</option>
            <option [value]="1">Visibility</option>
            <option [value]="2">Read-only</option>
            <option [value]="3">Mandatory</option>
          </select>
        </div>

        <div class="adf-search">
          <span class="adf-search-icon" aria-hidden="true">
            <svg class="icon-set">
              <use [attr.xlink:href]="'./assets/images/icons.svg#search'"></use>
            </svg>
          </span>
          <input type="text" class="adf-search-input"
            [placeholder]="viewMode === 'basic' ? 'Search groups & fields…' : 'Search rules…'"
            [(ngModel)]="searchText" (input)="onSearchChange()" (keyup)="onSearchChange()"
            [disabled]="!groupedDataFields || groupedDataFields.length === 0">
        </div>

        <div class="adf-paging" *ngIf="viewMode === 'advanced'">
          <button type="button" [disabled]="!pagingButtons.isFirst || !isDataLoaded"
            (click)="paginateRulesList('first')" title="First">
            <img src="assets/images/chevron-double-left.svg" alt="" />
          </button>
          <button type="button" [disabled]="!pagingButtons.isPrevious || !isDataLoaded"
            (click)="paginateRulesList('previous')" title="Previous">
            <img src="assets/images/chevron-left.svg" alt="" />
          </button>
          <span [class.disabled]="pagingDetails.total <= 0">
            {{ pagingDetails.start }}–{{ pagingDetails.end }} / {{ pagingDetails.total }}
          </span>
          <button type="button" [disabled]="!pagingButtons.isNext || !isDataLoaded"
            (click)="paginateRulesList('next')" title="Next">
            <img src="assets/images/chevron-right.svg" alt="" />
          </button>
          <button type="button" [disabled]="!pagingButtons.isLast || !isDataLoaded"
            (click)="paginateRulesList('last')" title="Last">
            <img src="assets/images/chevron-double-right.svg" alt="" />
          </button>
        </div>
      </div>
    </div>

    <div class="adf-info-banner" *ngIf="viewMode === 'advanced'">
      <div class="adf-info-banner-body">
        <span class="adf-info-dot" aria-hidden="true"></span>
        <span>
          Defaults: fields are <strong>visible</strong>, <strong>editable</strong>, and
          <strong>optional</strong>. Rules here hide fields, lock them, or make them mandatory.
        </span>
      </div>
      <button type="button" class="adf-icon-btn" (click)="closeBanner()" title="Dismiss">
        <svg class="icon-set">
          <use [attr.xlink:href]="'./assets/images/icons.svg#close'"></use>
        </svg>
      </button>
    </div>

    <!-- Basic View -->
    <div class="adf-fields" *ngIf="viewMode === 'basic'">
      <div *ngIf="!groupedDataFields || groupedDataFields.length === 0" class="adf-empty">
        <div class="adf-empty-visual" aria-hidden="true">
          <span></span><span></span><span></span>
        </div>
        <div class="adf-empty-title">Start with a field group</div>
        <div class="adf-empty-text">Groups hold related fields and can be reordered anytime.</div>
        <button type="button" [disabled]="readonly" class="adf-btn adf-btn--primary" (click)="addNewField()">
          New Group
        </button>
      </div>

      <div class="adf-groups"
        *ngIf="groupedDataFields?.length"
        cdkDropList
        id="data-field-groups"
        [cdkDropListData]="groupedDataFields"
        [cdkDropListConnectedTo]="[]"
        [cdkDropListDisabled]="isDragDisabled()"
        (cdkDropListDropped)="onGroupDrop($event)">
        <article class="adf-group" *ngFor="let field of groupedDataFields; let gi = index; trackBy: trackByFieldId"
          cdkDrag
          cdkDragLockAxis="y"
          [cdkDragData]="field"
          [cdkDragDisabled]="isDragDisabled()">
          <div class="field-drag-placeholder field-drag-placeholder--group" *cdkDragPlaceholder></div>

          <header class="adf-group-header">
            <div class="adf-group-header-left">
              <button type="button" class="adf-drag-handle" cdkDragHandle title="Drag to reorder group"
                [class.disabled]="isDragDisabled()" aria-label="Reorder group">
                <svg class="icon-set">
                  <use [attr.xlink:href]="'./assets/images/icons.svg#reorder'"></use>
                </svg>
              </button>
              <span class="adf-index" aria-hidden="true">{{ gi + 1 }}</span>
              <div class="adf-group-meta">
                <h3 class="adf-group-title">{{ field.display_name || field.name }}</h3>
                <span class="adf-group-count" *ngIf="field.hide_newField != true">
                  {{ field.children?.length || 0 }} field{{ (field.children?.length || 0) === 1 ? '' : 's' }}
                </span>
              </div>
            </div>
            <div class="adf-group-actions">
              <button type="button" class="adf-icon-btn" (click)="editField(field)" title="Edit group">
                <svg class="icon-set">
                  <use [attr.xlink:href]="'./assets/images/icons.svg#editIcon'"></use>
                </svg>
              </button>
              <button type="button" [disabled]="readonly" class="adf-icon-btn adf-icon-btn--danger" (click)="deleteField(field)"
                title="Delete group" *ngIf="!field.hide_delete">
                <svg class="icon-set">
                  <use [attr.xlink:href]="'./assets/images/icons.svg#trash-can-outline'"></use>
                </svg>
              </button>
            </div>
          </header>

          <div class="adf-group-body" *ngIf="field.hide_newField != true">
            <div class="adf-children"
              cdkDropList
              [id]="'child-list-' + field.field_id"
              [cdkDropListData]="field.children"
              [cdkDropListConnectedTo]="[]"
              [cdkDropListDisabled]="isDragDisabled()"
              (cdkDropListDropped)="onChildDrop($event, field)">
              <div class="adf-child" *ngFor="let child of field.children; let ci = index; trackBy: trackByFieldId"
                cdkDrag
                cdkDragLockAxis="y"
                [cdkDragData]="child"
                [cdkDragDisabled]="isDragDisabled()">
                <div class="field-drag-placeholder field-drag-placeholder--field" *cdkDragPlaceholder></div>
                <div class="adf-child-row">
                  <div class="adf-child-left">
                    <button type="button" class="adf-drag-handle adf-drag-handle--sm" cdkDragHandle
                      title="Drag to reorder field" [class.disabled]="isDragDisabled()"
                      aria-label="Reorder field">
                      <svg class="icon-set">
                        <use [attr.xlink:href]="'./assets/images/icons.svg#reorder'"></use>
                      </svg>
                    </button>
                    <span class="adf-child-index" aria-hidden="true">{{ ci + 1 }}</span>
                    <span class="adf-child-name">{{ child.display_name || child.name }}</span>
                    <span class="adf-type-chip" *ngIf="$any(child).data_type">{{ $any(child).data_type }}</span>
                  </div>
                  <div class="adf-child-actions">
                    <button type="button" class="adf-icon-btn" (click)="editField(child)" title="Edit field">
                      <svg class="icon-set">
                        <use [attr.xlink:href]="'./assets/images/icons.svg#editIcon'"></use>
                      </svg>
                    </button>
                    <button type="button" [disabled]="readonly" class="adf-icon-btn adf-icon-btn--danger" (click)="deleteField(child)"
                      title="Delete field" *ngIf="!child.hide_delete">
                      <svg class="icon-set">
                        <use [attr.xlink:href]="'./assets/images/icons.svg#trash-can-outline'"></use>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div class="adf-children-empty" *ngIf="!field.children?.length">
                No fields in this group yet.
              </div>
            </div>

            <button type="button" [disabled]="readonly" class="adf-add-field" *ngIf="!field.hide_newField"
              (click)="addNewField(field.field_id)">
              Add New field
            </button>
          </div>
        </article>
      </div>
    </div>

    <!-- Advanced View -->
    <div class="adf-rules" *ngIf="viewMode === 'advanced'">
      <div class="adf-rules-list" *ngIf="dataFieldRules?.length">
        <div class="adf-rule-card" *ngFor="let rule of dataFieldRules">
          <div class="adf-rule-main">
            <div class="adf-rule-title-row">
              <h4 class="adf-rule-name">{{ rule.name || rule.description }}</h4>
              <span class="adf-status-pill" [class.is-on]="rule.is_enabled" [class.is-off]="!rule.is_enabled">
                {{ rule.is_enabled ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <div class="adf-rule-meta">
              <span class="adf-rule-meta-text">Created : {{ rule.formatted_created_date || (rule.created_date | date:'dd-MMM-yy HH:mm') }}</span>
              <span class="adf-meta-dot" aria-hidden="true"></span>
              <span class="adf-rule-meta-text">Updated : {{ rule.formatted_last_updated || (rule.last_updated_on | date:'dd-MMM-yy HH:mm') }}</span>
            </div>
          </div>
          <div class="adf-rule-side">
            <label class="adf-switch" title="Toggle active">
              <input type="checkbox" [disabled]="readonly" [checked]="rule.is_enabled" (change)="enableRule(rule)">
              <span class="adf-switch-slider"></span>
            </label>
            <button type="button" class="adf-icon-btn" (click)="editRule(rule)" title="Edit rule">
              <svg class="icon-set">
                <use [attr.xlink:href]="'./assets/images/icons.svg#editIcon'"></use>
              </svg>
            </button>
            <button type="button" [disabled]="readonly" class="adf-icon-btn adf-icon-btn--danger" (click)="deleteRule(rule)"
              title="Delete rule">
              <svg class="icon-set">
                <use [attr.xlink:href]="'./assets/images/icons.svg#trash-can-outline'"></use>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div class="adf-empty" *ngIf="!dataFieldRules || !dataFieldRules.length">
        <div class="adf-empty-visual adf-empty-visual--rules" aria-hidden="true">
          <span></span><span></span>
        </div>
        <div class="adf-empty-title">No rules yet</div>
        <div class="adf-empty-text">Create a rule to control field visibility, editability, or mandatory state.</div>
        <button type="button" [disabled]="readonly" class="adf-btn adf-btn--primary" (click)="addNewField()">
          New Rule
        </button>
      </div>
    </div>
  </div>

  <div class="modal-overlay" *ngIf="isModalOpen" (click)="closeDataGroupModal()"></div>
  <aside class="right-modal" [class.open]="isModalOpen" aria-modal="true" role="dialog">
    <dynamic-action-form-renderer [readonly]="readonly" [schema]="modalSchema" [data]="defaultValues" (close)="closeDataGroupModal()"
      (submit)="onFormSubmit($event)">
    </dynamic-action-form-renderer>
  </aside>

  <div class="modal-overlay" *ngIf="isChildFieldModalOpen" (click)="closeChildFieldModal()"></div>
  <aside class="right-modal" [class.open]="isChildFieldModalOpen" aria-modal="true" role="dialog">
    <dynamic-action-form-renderer [readonly]="readonly" [schema]="childFieldModalSchema" [data]="childFieldDefaultValues"
      (close)="closeChildFieldModal()" (submit)="onChildFieldFormSubmit($event)">
    </dynamic-action-form-renderer>
  </aside>

  <div class="modal-overlay" *ngIf="isRuleModalOpen" (click)="closeRuleModal()"></div>
  <aside class="right-modal" [class.open]="isRuleModalOpen" aria-modal="true" role="dialog">
    <dynamic-action-form-renderer [readonly]="readonly" [schema]="ruleModalSchema" [data]="ruleDefaultValues" (close)="closeRuleModal()"
      (submit)="onRuleFormSubmit($event)">
    </dynamic-action-form-renderer>
  </aside>
</div>
